import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Col, Input, Row, Select } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import CustomButton from "@/components/CustomButton";

import Modal from "@components/Modal";
import { useTypedSelector } from "@hooks";
import { ICountry, IModal, IRegion } from "@types";
import {
  ErrorServices,
  useCreateRegionMutation,
  useFetchAllBusinessUnitsQuery,
  useUpdateRegionMutation,
} from "@services";
import { showSuccessPopup, sortCountriesAlphabetically } from "@utils";

const { Option } = Select;

interface IAddRegion extends IModal {
  selectedRegion: IRegion | null;
  setSelectedRegion: React.Dispatch<React.SetStateAction<IRegion | null>>;
}

const INITIAL_STATE: IRegion = {
  name: "",
  countries: [],
  business_units: [],
};

const AddRegion: React.FC<IAddRegion> = ({
  selectedRegion,
  setSelectedRegion,
  isVisible,
  setIsVisible,
}) => {
  const { company_id } = useParams<{ company_id: string }>();

  const [region, setRegion] = useState<IRegion>(INITIAL_STATE);

  const { data: businessUnits } = useFetchAllBusinessUnitsQuery({
    company_id,
  });

  const { data } = businessUnits || {};

  const [createRegion, { isLoading: isCreating }] = useCreateRegionMutation();

  const [updateRegion, { isLoading: isUpdating }] = useUpdateRegionMutation();

  const { countries: countriesList } = useTypedSelector(
    state => state.countries
  );

  const {
    name,
    countries: country_ids,
    business_units: business_unit_ids,
    id,
  } = region;

  useEffect(() => {
    if (selectedRegion) {
      setRegion({
        ...selectedRegion,
        countries: selectedRegion.countries.map((country: any) => country?.id),
        business_units: selectedRegion.business_units.map(
          (unit: any) => unit?.id
        ),
      } as IRegion);
    } else {
      setRegion(INITIAL_STATE);
    }

    return () => {
      setSelectedRegion(null);
    };
  }, [selectedRegion, setSelectedRegion]);

  const onSubmit = async () => {
    const payload = {
      body: {
        id,
        name: name.trim(),
        country_ids,
        business_unit_ids,
      },
      company_id,
    };

    try {
      const result: any = selectedRegion
        ? await updateRegion(payload)
        : await createRegion(payload);

      console.log("Region API result:", result);

      // RTK Query returns HTTP 4xx/5xx inside `error`
      if (result?.error) {
        console.error("Region API error:", result.error);

        ErrorServices(result.error);
        return;
      }

      // Extra guard if backend ever responds 200 with success: false
      if (result?.data?.success === false) {
        console.error("Region backend error:", result.data);

        ErrorServices({
          data: result.data,
        });

        return;
      }

      // Only runs on actual success
      showSuccessPopup({
        title: `Region ${selectedRegion ? "Updated" : "Created"}`,
        desc: `You have successfully ${
          selectedRegion ? "updated" : "created"
        } a region.`,
      });

      setIsVisible(false);
      setRegion(INITIAL_STATE);
      setSelectedRegion(null);
    } catch (err: any) {
      console.error("Unexpected region error:", err);

      ErrorServices(err);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setRegion(INITIAL_STATE);
    setSelectedRegion(null);
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal
      footer={[
        <CustomButton
          key="1"
          onClick={onSubmit}
          disabledProp={!region.name.trim().length || isSubmitting}
          btnName={
            isSubmitting ? (
              <LoadingOutlined className="spinner" />
            ) : (
              `${selectedRegion ? "Update" : "Create"} Region`
            )
          }
        >
          {isSubmitting ? (
            <LoadingOutlined className="spinner" />
          ) : (
            `${selectedRegion ? "Update" : "Create"} Region`
          )}
        </CustomButton>,

        <CustomButton key="2" onClick={handleClose} btnName="Cancel">
          Cancel
        </CustomButton>,
      ]}
      title={`${selectedRegion ? "Update" : "Create"} a Region`}
      isVisible={isVisible}
      onClose={handleClose}
    >
      <>
        <Row className="modal__row">
          <Col span={11}>
            <label>Name of a region</label>

            <Input
              onChange={e =>
                setRegion(prev => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              placeholder="Enter region name here..."
              size="large"
              value={region.name}
            />
          </Col>
        </Row>

        <div className="sub-heading mb-32">
          Add Countries and Business Units
        </div>

        <Row justify="space-between">
          <Col span={11}>
            <label>Select countries</label>

            <Select
              value={region.countries as number[]}
              size="large"
              showArrow
              mode="multiple"
              placeholder="Select countries from here..."
              showSearch
              filterOption={(input: any, option: any) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(val: number[]) =>
                setRegion(prev => ({
                  ...prev,
                  countries: val,
                }))
              }
            >
              {sortCountriesAlphabetically(countriesList)?.map(
                ({ name, id }: ICountry) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                )
              )}
            </Select>
          </Col>

          <Col span={11}>
            <label>Select business units</label>

            <Select
              value={region.business_units as number[]}
              size="large"
              showArrow
              mode="multiple"
              placeholder="Select business units from here..."
              showSearch={false}
              onChange={(val: number[]) =>
                setRegion(prev => ({
                  ...prev,
                  business_units: val,
                }))
              }
            >
              {data?.map(({ name, id }: { name: string; id: number }) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </>
    </Modal>
  );
};

export default AddRegion;
